// ConfirmDialog + UndoToast — Worksnip's app-wide confirmation modal and
// undoable-action toast. One file because the two are conceptually paired:
// every destructive flow is "confirm → execute → toast with undo".
//
// Usage from anywhere (no prop drilling, no context):
//
//   import { confirmDialog, showUndoToast, ConfirmHost } from '@/components/ui/ConfirmDialog';
//
//   const ok = await confirmDialog({
//     title: 'Delete capture?',
//     message: 'It moves to Archived. You can undo within 5 seconds.',
//     confirmLabel: 'Delete',
//     danger: true,
//   });
//   if (ok) {
//     await api.patch(`/guides/${id}`, { status: 'archived' });
//     showUndoToast({
//       message: 'Capture deleted',
//       onUndo: () => api.patch(`/guides/${id}`, { status: previous }),
//     });
//   }
//
// Mount <ConfirmHost /> once at the app root (AppShell) and the rest is
// imperative — `confirmDialog(...)` returns a Promise<boolean>. We avoid
// `window.confirm` because the project's tokens + accessibility story
// demand a styled modal that matches `ws-tile` + `--ws-danger`.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../../lib/useFocusTrap';

// ── confirm dialog ────────────────────────────────────────────────────────

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the primary button with --ws-danger styling. */
  danger?: boolean;
}

/** One radio option in a `confirmChoice` dialog. */
export interface ConfirmChoice {
  id: string;
  label: string;
  /** Second line under the label — say what actually happens to the data. */
  description?: string;
  /** Tints the option when selected, for the destructive branch. */
  danger?: boolean;
}

export interface ConfirmChoiceOptions extends ConfirmOptions {
  choices: ConfirmChoice[];
  /** Pre-selected option. Defaults to the first, so the safe branch goes first. */
  defaultChoiceId?: string;
}

type ConfirmRequest = ConfirmOptions & {
  choices?: ConfirmChoice[];
  defaultChoiceId?: string;
  resolve: (result: boolean | string | null) => void;
};

// Module-scoped subscriber list — the host component registers a setter
// here on mount; `confirmDialog()` pushes a request and waits for the
// user's choice. Only one dialog can be open at a time.
let confirmSubscriber: ((req: ConfirmRequest | null) => void) | null = null;

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!confirmSubscriber) {
      // Fail-open if the host isn't mounted — never block the user.
      // Should never happen in production (AppShell mounts <ConfirmHost />),
      // but unit-test environments might call this without the host.
      console.warn('[ConfirmDialog] no host mounted; auto-confirming');
      resolve(true);
      return;
    }
    confirmSubscriber({ ...opts, resolve: (r) => resolve(r === true) });
  });
}

/**
 * Confirm, but the user also picks HOW. Resolves the chosen option's id, or
 * null if they cancelled. Used where a destructive action has more than one
 * defensible outcome — deleting a folder that still holds worksnips.
 *
 * Unlike `confirmDialog` this FAILS CLOSED with no host mounted: silently
 * auto-picking a branch of a destructive choice is never the safe default.
 */
export function confirmChoice(opts: ConfirmChoiceOptions): Promise<string | null> {
  return new Promise((resolve) => {
    if (!confirmSubscriber) {
      console.warn('[ConfirmDialog] no host mounted; cancelling choice');
      resolve(null);
      return;
    }
    confirmSubscriber({
      ...opts,
      resolve: (r) => resolve(typeof r === 'string' ? r : null),
    });
  });
}

// ── undo toast ────────────────────────────────────────────────────────────

export interface UndoToastOptions {
  message: string;
  /** Called when the user clicks the Undo button. Awaited if it returns a
   *  Promise; the toast dismisses immediately on click regardless. */
  onUndo?: () => void | Promise<void>;
  /** Auto-dismiss in ms. Defaults to 5000. */
  durationMs?: number;
}

interface ActiveToast extends UndoToastOptions {
  id: number;
}

let toastSubscriber: ((t: ActiveToast | null) => void) | null = null;
let toastCounter = 0;

export function showUndoToast(opts: UndoToastOptions): void {
  if (!toastSubscriber) {
    console.warn('[UndoToast] no host mounted; toast suppressed');
    return;
  }
  toastCounter += 1;
  toastSubscriber({ ...opts, id: toastCounter });
}

// ── single mount point — drop into AppShell ───────────────────────────────

export function ConfirmHost() {
  const [req, setReq] = useState<ConfirmRequest | null>(null);
  const [toast, setToast] = useState<ActiveToast | null>(null);

  useEffect(() => {
    confirmSubscriber = setReq;
    toastSubscriber = setToast;
    return () => {
      confirmSubscriber = null;
      toastSubscriber = null;
    };
  }, []);

  return (
    <>
      {req && (
        <ConfirmModal
          opts={req}
          onClose={(ok, choiceId) => {
            // A choice dialog resolves the picked id (or null); a plain
            // confirm resolves the boolean. `confirmDialog`/`confirmChoice`
            // each narrow this back to their own return type.
            req.resolve(req.choices ? (ok ? choiceId ?? null : null) : ok);
            setReq(null);
          }}
        />
      )}
      {toast && (
        <UndoToast
          toast={toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

// ── modal ─────────────────────────────────────────────────────────────────

interface ConfirmModalProps {
  opts: ConfirmRequest;
  onClose: (ok: boolean, choiceId?: string) => void;
}

function ConfirmModal({ opts, onClose }: ConfirmModalProps) {
  const { t } = useTranslation('common');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [choiceId, setChoiceId] = useState<string>(
    opts.defaultChoiceId ?? opts.choices?.[0]?.id ?? '',
  );
  const activeChoice = opts.choices?.find((c) => c.id === choiceId);
  // The primary button inherits the *selected branch's* danger, not the
  // dialog's — "move them to Inbox" must not be painted as a destroy button.
  const primaryDanger = opts.choices ? !!activeChoice?.danger : !!opts.danger;

  // A11Y-1 — focus trap now owns: initial focus (danger flows focus Cancel
  // first — the safer default; non-danger focuses Confirm, matching the
  // OS-native confirm-dialog muscle memory), Tab-cycling, Escape-to-close,
  // and focus restore on close. ConfirmModal only ever mounts while a
  // request is pending (see ConfirmHost above), so `active: true` for its
  // whole lifetime; unmount is the deactivation.
  useFocusTrap(dialogRef, {
    active: true,
    onClose: () => onClose(false),
    initialFocusRef: opts.danger ? cancelRef : confirmRef,
  });

  // Enter-to-confirm is bespoke business logic (not a generic dialog
  // behavior), so it stays as its own handler alongside the focus trap.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter' && !opts.danger) { e.preventDefault(); onClose(true); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [opts.danger, onClose]);

  // Render in a portal so the modal escapes any transformed/clipped parent.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ws-confirm-title"
      className="fixed inset-0 z-[100] grid place-items-center ws-anim-fade-up"
      onClick={() => onClose(false)}
      style={{ background: 'var(--ws-overlay)', animationDuration: '120ms' }}
    >
      <div
        ref={dialogRef}
        className="ws-tile relative w-full max-w-md mx-4 p-5"
        style={{ boxShadow: 'var(--ws-shadow-lg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label={t('common:actions.close')}
          className="absolute top-3 right-3 w-7 h-7 rounded-full inline-flex items-center justify-center text-ws-ink-3 hover:text-ws-ink hover:bg-ws-surface-2"
          onClick={() => onClose(false)}
        >
          <X size={14} strokeWidth={2} />
        </button>

        <div className="flex items-start gap-3">
          {opts.danger && (
            <span
              aria-hidden
              className="flex-shrink-0 w-9 h-9 rounded-full grid place-items-center"
              style={{
                background: 'color-mix(in oklch, var(--ws-danger) 14%, transparent)',
                color: 'var(--ws-danger)',
              }}
            >
              <AlertTriangle size={18} strokeWidth={1.75} />
            </span>
          )}
          <div className="flex-1 min-w-0 pr-6">
            <h2 id="ws-confirm-title" className="text-base font-semibold text-ws-ink leading-tight">
              {opts.title}
            </h2>
            {opts.message && (
              <p className="mt-1.5 text-sm text-ws-ink-2 leading-relaxed">
                {opts.message}
              </p>
            )}
          </div>
        </div>

        {opts.choices && (
          <div className="mt-4 flex flex-col gap-2" role="radiogroup" aria-label="What to do with the contents">
            {opts.choices.map((c) => {
              const active = c.id === choiceId;
              return (
                <label
                  key={c.id}
                  className="flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer transition-colors"
                  style={{
                    borderColor: active
                      ? (c.danger ? 'var(--ws-danger)' : 'var(--ws-accent)')
                      : 'var(--ws-border)',
                    background: active
                      ? `color-mix(in oklch, ${c.danger ? 'var(--ws-danger)' : 'var(--ws-accent)'} 8%, transparent)`
                      : 'transparent',
                  }}
                >
                  <input
                    type="radio"
                    name="ws-confirm-choice"
                    className="mt-0.5"
                    checked={active}
                    onChange={() => setChoiceId(c.id)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ws-ink">{c.label}</span>
                    {c.description && (
                      <span className="block mt-0.5 text-xs text-ws-ink-3 leading-relaxed">{c.description}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            className="ws-btn-tertiary text-sm"
            onClick={() => onClose(false)}
          >
            {opts.cancelLabel || t('common:actions.cancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            disabled={!!opts.choices && !choiceId}
            className="text-sm font-medium px-3 py-1.5 rounded-md text-white disabled:opacity-50"
            style={{
              background: primaryDanger ? 'var(--ws-danger)' : 'var(--ws-accent)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
            onClick={() => onClose(true, choiceId)}
          >
            {opts.confirmLabel || (opts.danger ? t('common:actions.delete') : t('common:actions.confirm'))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── toast ─────────────────────────────────────────────────────────────────

interface UndoToastProps {
  toast: ActiveToast;
  onDismiss: () => void;
}

function UndoToast({ toast, onDismiss }: UndoToastProps) {
  const { t } = useTranslation('common');
  const duration = toast.durationMs ?? 5000;
  const [remaining, setRemaining] = useState(duration);

  // Tick the countdown so the progress bar tracks reality. We re-render
  // every 100ms — cheap enough, smooth enough.
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const left = Math.max(0, duration - (Date.now() - start));
      setRemaining(left);
      if (left <= 0) {
        clearInterval(id);
        onDismiss();
      }
    }, 100);
    return () => clearInterval(id);
  }, [duration, onDismiss, toast.id]);

  const pct = Math.max(0, Math.min(100, (remaining / duration) * 100));

  const handleUndo = () => {
    try {
      const ret = toast.onUndo?.();
      if (ret && typeof (ret as Promise<void>).then === 'function') {
        (ret as Promise<void>).catch((e) => console.error('[UndoToast] undo failed', e));
      }
    } catch (e) {
      console.error('[UndoToast] undo threw', e);
    }
    onDismiss();
  };

  return createPortal(
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] ws-anim-fade-up"
      style={{ animationDuration: '160ms' }}
      role="status"
      aria-live="polite"
    >
      <div
        className="relative flex items-center gap-3 pl-4 pr-2 py-2 rounded-md border overflow-hidden"
        style={{
          background: 'var(--ws-tile-bg)',
          borderColor: 'var(--ws-tile-border)',
          boxShadow: 'var(--ws-shadow-lg)',
          minWidth: 280,
        }}
      >
        <span className="text-sm text-ws-ink font-medium">{toast.message}</span>
        {toast.onUndo && (
          <>
            <span aria-hidden className="text-ws-ink-3">·</span>
            <button
              type="button"
              onClick={handleUndo}
              className="text-sm font-semibold px-2 py-1 rounded-sm hover:bg-ws-surface-2"
              style={{ color: 'var(--ws-accent-ink)' }}
            >
              {t('common:actions.undo')}
            </button>
          </>
        )}
        <button
          type="button"
          aria-label={t('common:actions.dismiss')}
          onClick={onDismiss}
          className="ml-1 w-6 h-6 rounded-full inline-flex items-center justify-center text-ws-ink-3 hover:text-ws-ink hover:bg-ws-surface-2"
        >
          <X size={12} strokeWidth={2} />
        </button>
        {/* Countdown bar — drains left-to-right so the user can see how
            much undo time remains. Uses the accent token so it reads as
            "you can still act" rather than a danger color. */}
        <span
          aria-hidden
          className="absolute left-0 bottom-0 h-[2px] transition-[width]"
          style={{
            width: `${pct}%`,
            background: 'var(--ws-accent)',
            transitionDuration: '100ms',
            transitionTimingFunction: 'linear',
          }}
        />
      </div>
    </div>,
    document.body,
  );
}
